import * as React from 'react';
import { observer } from 'mobx-react';
import { PayHistoryChangeEntry } from 'nomst/Stores/Decision/payHistoryChangeEntry';
import { PayHistory } from 'nomst/Stores/Decision/payHistory';
import { Format } from 'nomst/Stores/Utility/format';
import { currencyConversion } from 'utils/MobxDataManager/singletons';
import { dateFormatYearOnly, dateFormatWithoutYear } from 'common/date-operation';
import { PayHistoryChangeEntryDetailsView } from './payHistoryChangeEntryDetailsView';
import './payHistoryChangeEntryView.scss';

export interface IPayHistoryChangeEntryViewProps {
    shouldRenderYear: boolean;
    payHistory?: PayHistory;
    payHistoryChangeEntry?: PayHistoryChangeEntry;
    year?: number;
    text?: string;
    format: Format;
    selectedCurrency?: string;
}

export interface IPayHistoryChangeEntryViewState {
    showDetails: boolean;
}

export class PayHistoryChangeEntryView
    extends React.Component<IPayHistoryChangeEntryViewProps, IPayHistoryChangeEntryViewState> {

    constructor(props: IPayHistoryChangeEntryViewProps) {
        super(props);
        this.state = { showDetails: false };

        this.toggleShowDetails = this.toggleShowDetails.bind(this);
    }

    render(): React.ReactNode {
        const { payHistory, payHistoryChangeEntry, year, text } = this.props;
        if (payHistoryChangeEntry) {
            return this.renderPayHistoryChangeEntry(payHistoryChangeEntry);
        }

        if (payHistory && payHistory.hireDate) {
            return this.renderHiredOnEntry();
        }

        if (year && text) {
            return this.renderEmptyPayHistoryChangeEntry(year, text);
        }

        return null;
    }

    private renderHiredOnEntry(): React.ReactNode {
        const { payHistory } = this.props;
        if (!payHistory) {
            return null;
        }

        const currency = payHistory.isoCurrencyCode || '';
        const hireDate = payHistory.hireDate || '';

        return (
            <div className="nomst-pay-history-change-entry">
                <div className="row hired-on">
                    <div className="col-xs-3 col-md-3">
                        <div className="row">
                            <div className="col-xs-6 col-md-6">
                                <h5>Hired on</h5>
                                <i className="icon-handshake-o" />
                            </div>
                            <div className="offset-xs-6 offset-md-6">
                                {this.renderPayEffectiveDateYear(dateFormatYearOnly(hireDate))}
                                <h4>{dateFormatWithoutYear(hireDate)}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="col-xs-9 col-md-9">
                        <div className="row">
                            <div className="col-xs-6 col-md-6">
                                <h5>Base Pay</h5>
                                <div
                                    data-mf-replace-inner="***"
                                    className="pay-change-value"
                                >
                                    {this.getValueDisplayText(
                                        payHistory.annualBasePay(),
                                        currency,
                                        payHistory.isHourly
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderEmptyPayHistoryChangeEntry(year: number, text: string): React.ReactNode {
        return (
            <div className="nomst-pay-history-change-entry">
                <div className="row no-change">
                    <div className="col-xs-3 col-md-3">
                        <h3>{year}</h3>
                    </div>
                    <div className="col-xs-9 col-md-9">
                        <div className="pay-changes">{text}</div>
                    </div>
                </div>
            </div>
        );
    }

    private renderPayHistoryChangeEntry(payHistoryChangeEntry: PayHistoryChangeEntry): React.ReactNode {
        return (
            <div className="nomst-pay-history-change-entry">
                <div className={this.state.showDetails ? 'row change open' : 'row change'}>
                    <div className="col-xs-3 col-md-3">
                        {this.renderPayEffectiveDateYear(dateFormatYearOnly(payHistoryChangeEntry.payEffectiveDate))}
                        <h4>{dateFormatWithoutYear(payHistoryChangeEntry.payEffectiveDate)}</h4>
                    </div>
                    <div className="col-xs-9 col-md-9">
                        <div className="pay-changes">
                            <div className="row pay-changes__row">
                                <div className="col-xs-6 col-md-6">
                                    <h5>Base Pay</h5>
                                    <div
                                        data-mf-replace-inner="***"
                                        className="pay-change-value"
                                    >
                                        {this.getValueDisplayText(
                                            payHistoryChangeEntry.annualBasePay,
                                            payHistoryChangeEntry.isoCurrencyCode,
                                            payHistoryChangeEntry.isHourly
                                        )}
                                    </div>
                                </div>
                                <div className="col-xs-6 col-md-6">
                                    <h5>TCC</h5>
                                    <div
                                        data-mf-replace-inner="***"
                                        className="pay-change-value"
                                    >
                                        {this.getValueDisplayText(
                                            payHistoryChangeEntry.tcc,
                                            payHistoryChangeEntry.isoCurrencyCode,
                                            payHistoryChangeEntry.isHourly
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div
                                className="pay-changes__amount-titles"
                            >
                                {this.getAmountsText()}
                            </div>
                            <div className="view-details-button">
                                {this.renderViewDetailsButton()}
                            </div>
                            {this.renderPayHistoryDetailsView(payHistoryChangeEntry)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderPayEffectiveDateYear(year: string): React.ReactNode {
        const { shouldRenderYear } = this.props;
        if (shouldRenderYear === false) {
            return null;
        }

        return (<h3>{year}</h3>);
    }

    private renderViewDetailsButton(): React.ReactNode {
        if (this.state.showDetails) {
            return (
                <button onClick={this.toggleShowDetails} className="history-item__toggle pxl-link">
                Hide details <i className="icon-sort-up" /></button>
            );
        }

        return (
            <button onClick={this.toggleShowDetails}className="history-item__toggle pxl-link">
            View details <i className="icon-sort-down" /></button>
        );
    }

    private renderPayHistoryDetailsView(payHistoryChangeEntry: PayHistoryChangeEntry): React.ReactNode {
        if (this.state.showDetails === false) {
            return null;
        }

        return (
            <PayHistoryChangeEntryDetailsView
                payHistoryChangeEntry={payHistoryChangeEntry}
                key={`${payHistoryChangeEntry.payHistoryChangeEntryId}`}
                format={this.props.format}
                selectedCurrency={this.props.selectedCurrency}
            />
        );
    }

    private toggleShowDetails(): void {
        const previousShowDetails = this.state.showDetails;
        this.setState({ showDetails: !previousShowDetails });
    }

    private getValueDisplayText(value: number, sourceCurrency: string, isHourly: boolean): string {
        const { selectedCurrency, format } = this.props;
        let convertedValue = value;
        let displayCurrency = sourceCurrency;
        if (selectedCurrency) {
            convertedValue = currencyConversion.convertCurrency(sourceCurrency, selectedCurrency, value);
            displayCurrency = selectedCurrency;
        }
        return format.formatCurrencyValue(displayCurrency, convertedValue, isHourly);
    }

    private getAmountsText(): Nullable<string> {
        const { payHistoryChangeEntry } = this.props;
        if (payHistoryChangeEntry && payHistoryChangeEntry.amounts) {
            return payHistoryChangeEntry.amounts.map(amount => amount.title).join(', ');
        }

        return null;
    }
}

export default observer(PayHistoryChangeEntryView);